import { useEffect } from 'react'

/**
 * useFocusTrap
 * Traps keyboard focus within a container while `active` is true.
 * It preserves natural tab order and wraps focus on Tab/Shift+Tab at edges.
 */
export function useFocusTrap(container: React.RefObject<HTMLElement | null>, active: boolean) {
  useEffect(() => {
    if (!active) return
    const root = container.current
    if (!root) return

    const FOCUSABLE_SELECTOR = [
      'a[href]',
      'area[href]',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'button:not([disabled])',
      'iframe',
      'object',
      'embed',
      '[contenteditable]',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',')

    const getFocusable = () => {
      const elements = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
      return elements.filter(el => !el.hasAttribute('disabled') && !el.getAttribute('aria-hidden'))
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return
      const focusable = getFocusable()
      if (focusable.length === 0) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const current = document.activeElement as HTMLElement | null

      if (e.shiftKey) {
        if (!current || current === first || !root.contains(current)) {
          last.focus()
          e.preventDefault()
        }
      } else {
        if (!current || current === last || !root.contains(current)) {
          first.focus()
          e.preventDefault()
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [container, active])
}


